import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Globe, CheckCircle2, XCircle, PlusCircle, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas/bank-discovery`;

const Evidence = ({ e }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] mr-2 ${e.reachable ? 'text-emerald-300' : 'text-red-300'}`} title={e.error || e.title || ''}>
    {e.reachable ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}{e.source_field} {e.status_code || ''}
  </span>
);

const InstCard = ({ rid, inst, onRegister }) => (
  <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 space-y-1" data-testid={`discovery-inst-${inst.identifier}`}>
    <div className="flex items-center justify-between gap-2">
      <div>
        <p className="text-white text-sm font-semibold">{inst.name} <span className="text-slate-500 text-xs font-mono">{inst.identifier}</span></p>
        {inst.developer_portal && <a href={inst.developer_portal} target="_blank" rel="noreferrer" className="text-xs text-cyan-300 flex items-center gap-1 break-all"><Globe className="w-3 h-3" />{inst.developer_portal}</a>}
      </div>
      <button onClick={() => onRegister(rid, inst.identifier)} className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs flex items-center gap-1 whitespace-nowrap" data-testid={`discovery-register-${inst.identifier}`}><PlusCircle className="w-3 h-3" /> Add to directory</button>
    </div>
    {!!(inst.api_services || []).length && <p className="text-[11px] text-slate-400">Services: {inst.api_services.join(', ')}</p>}
    {!!(inst.auth_methods || []).length && <p className="text-[11px] text-slate-400">Auth: {inst.auth_methods.join(', ')}</p>}
    <div className="pt-1">{(inst.evidence || []).map((e, i) => <Evidence key={i} e={e} />)}</div>
    {inst.partnership_info && <p className="text-[11px] text-slate-500 italic">{inst.partnership_info}</p>}
  </div>
);

export const BankApiDiscovery = ({ token, onRegistered }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => { axios.get(`${API}/results`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setResults(r.data.results)).catch(() => {}); }, [token]);
  useEffect(() => { load(); }, [load]);

  const search = async () => {
    if (query.trim().length < 2) return;
    setLoading(true);
    try { const r = await axios.post(`${API}/search`, { query, limit: 6 }, h); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Discovery failed'); }
    setLoading(false);
  };
  const register = async (rid, identifier) => {
    try { const r = await axios.post(`${API}/${rid}/register`, { identifier }, h); toast.success(r.data.message); onRegistered && onRegistered(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="bank-api-discovery">
      <div className="bg-slate-800 border border-cyan-500/30 rounded-xl p-4 space-y-3">
        <p className="text-xs text-slate-400">Search for real banks / banking-API providers. Results are crawled for evidence but are <b>unverified candidates</b> — discovering a portal does not grant any access.</p>
        <div className="flex flex-wrap gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()} placeholder="e.g. ACH and wire banking API for fintech" className="flex-1 min-w-[240px] px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm" data-testid="discovery-query" />
          <button disabled={loading || query.trim().length < 2} onClick={search} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-lg text-sm font-semibold flex items-center gap-1" data-testid="discovery-search-btn">
            {loading ? <RotateCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>
      {results.map(r => (
        <div key={r.id} className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-2" data-testid={`discovery-result-${r.id}`}>
          <p className="text-white text-sm font-semibold flex items-center gap-2"><Search className="w-4 h-4 text-cyan-400" /> "{r.query}" <span className="text-slate-500 text-xs">· {r.count} found · {new Date(r.created_at).toLocaleString()}</span></p>
          <div className="grid gap-2 md:grid-cols-2">{r.institutions.map((inst, i) => <InstCard key={i} rid={r.id} inst={inst} onRegister={register} />)}</div>
        </div>
      ))}
    </div>
  );
};
